import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, TextField, Stack, FormControlLabel, Switch, Alert, SxProps, Theme,
} from '@mui/material';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { useSnackbar } from 'notistack';
import apiClient from '../../../requests/api/apiClient';
import type { PeakHourModalProps } from './riego.types';
import { DEFAULT_END, DEFAULT_INIT, TIME_INPUT_STEP_SECONDS } from './riego.constants';

const timeFieldSx: SxProps<Theme> = { flex: '1 1 120px' };

const PeakHourModal: React.FC<PeakHourModalProps> = ({
  open, onClose, onSaved, farmId, pumpSystemId, pumpSystemName, current,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [enabled, setEnabled] = useState<boolean>(false);
  const [init, setInit] = useState<string>(DEFAULT_INIT);
  const [end, setEnd] = useState<string>(DEFAULT_END);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setEnabled(current?.enabled ?? false);
    setInit(current?.init || current?.wiseconnInit || DEFAULT_INIT);
    setEnd(current?.end || current?.wiseconnEnd || DEFAULT_END);
  }, [open, current]);

  const handleSave = async () => {
    if (enabled && (!init || !end)) {
      setError('Indica la hora de inicio y de fin de la franja.');
      return;
    }
    if (enabled && init === end) {
      setError('La hora de inicio y de fin no pueden ser iguales.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiClient.peakHour.setPeakHour(farmId, pumpSystemId, { enabled, init, end });
      enqueueSnackbar(
        enabled
          ? `Hora punta configurada para ${pumpSystemName} (${init}–${end}).`
          : `Hora punta desactivada para ${pumpSystemName}.`,
        { variant: 'success' },
      );
      onSaved();
      onClose();
    } catch (err) {
      const e = err as { status?: number; error?: { message?: string }; message?: string };
      if (e?.status === 403) {
        enqueueSnackbar('Solo el administrador puede configurar la hora punta.', {
          variant: 'error',
        });
        onClose();
        return;
      }
      setError(e?.error?.message || e?.message || 'No se pudo guardar la hora punta.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleOutlinedIcon fontSize="small" sx={{ color: 'warning.main' }} />
        Hora punta · {pumpSystemName}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Define la franja en la que NO se podrá programar riego para este equipo.
          La validación se aplica a programaciones manuales y automáticas.
        </DialogContentText>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2}>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Activar hora punta"
          />
          <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Inicio"
              type="time"
              value={init}
              onChange={(e) => setInit(e.target.value)}
              disabled={!enabled}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: TIME_INPUT_STEP_SECONDS }}
              sx={timeFieldSx}
            />
            <TextField
              label="Fin"
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              disabled={!enabled}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: TIME_INPUT_STEP_SECONDS }}
              sx={timeFieldSx}
            />
          </Stack>
          {enabled && init > end && (
            <Alert severity="info">
              La franja cruza la medianoche ({init}–{end} del día siguiente).
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" color="warning" disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PeakHourModal;
